import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { motion } from 'motion/react';
import JobCreationForm from '../../components/JobCreationForm';
import GoColorsPayrollDownloadMenu from '../../components/screenings/GoColorsPayrollDownloadMenu';
import { normaliseStoreFormat, STORE_FORMATS } from '../../utils/storeFormat';
import AnimatedModal from '../../components/ui/AnimatedModal';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission, JOB_PERMISSIONS } from '../../lib/permissions';
import { useJobs, JOBS_QUERY_KEY } from '../../hooks/useJobs';
import useBodyScrollLock from 'src/hooks/useBodyScrollLock';
import {
  TrashIcon,
  XMarkIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  BriefcaseIcon,
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const ResultsAnalytics = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSamtaGroup = String(user?.orgId) === '6a3b9faeb52a49d66cc5f63e';
  const isGoColors = user?.orgSlug === 'go-colors---go-fashion-india-ltd-sdf61l';
  const isBurgerKing = user?.orgSlug === 'b-pia1dr';
  const canCreateJob = hasPermission(user, JOB_PERMISSIONS.CREATE);
  const canCloseJob = hasPermission(user, JOB_PERMISSIONS.CLOSE);
  const canReopenJob = hasPermission(user, JOB_PERMISSIONS.REOPEN);
  const isOrgOrPlatformAdmin =
    user?.accountType === 'platform' ||
    (user?.accountType === 'org' && user?.role === 'admin');
  const canDeleteJob = isOrgOrPlatformAdmin && hasPermission(user, JOB_PERMISSIONS.DELETE);
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(null);
  const [showReopenConfirm, setShowReopenConfirm] = useState(null);
  const [closingJobId, setClosingJobId] = useState(null);
  const [reopeningJobId, setReopeningJobId] = useState(null);
  const [closeReasons, setCloseReasons] = useState([]);
  const [closeNote, setCloseNote] = useState('');

  const CLOSE_REASON_OPTIONS = [
    'Found the desired candidate',
    'Position filled internally',
    'Hiring put on hold',
    'Role requirements changed',
    'Budget constraints / cost cutting',
    'No longer hiring for this role',
  ];
  const [showJobCreationModal, setShowJobCreationModal] = useState(false);
  useBodyScrollLock(
    Boolean(showDeleteConfirm) || Boolean(showCloseConfirm) || Boolean(showReopenConfirm),
  );
  const [jobFormData, setJobFormData] = useState({
    title: '',
    description: '',
    keyResponsibilities: '',
    experience: '',
    skills: '',
    keyAttributes: '',
    location: '',
    locationType: 'remote',
    noticePeriod: '30 days',
    experienceMin: '',
    experienceMax: '',
    salaryMin: '',
    salaryMax: '',
    salaryCurrency: 'USD',
    education: '',
    candidateQualification: '',
    genderPreference: 'any',
    ageMin: '',
    ageMax: '',
    storeFormat: 'EBO',
  });
  const [showJobForm, setShowJobForm] = useState(false);
  const [openActionJobId, setOpenActionJobId] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const actionButtonRefs = useRef({});
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'closed'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters] = useState(false);
  const tabsRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  // Store format filter for Go Colors
  const [storeFormatFilter, setStoreFormatFilter] = useState(
    () => localStorage.getItem('gocolors_store_format_filter') || 'All'
  );

  useEffect(() => {
    if (isGoColors) {
      localStorage.setItem('gocolors_store_format_filter', storeFormatFilter);
    }
  }, [storeFormatFilter, isGoColors]);

  // Close actions menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the action button and menu
      const clickedButton = Object.values(actionButtonRefs.current).some(
        (ref) => ref && ref.contains(event.target)
      );
      const clickedMenu = event.target.closest('[data-action-menu]');

      if (!clickedButton && !clickedMenu && openActionJobId) {
        setOpenActionJobId(null);
        setActionMenuPosition(null);
      }
    };

    const handleScroll = () => {
      if (openActionJobId) {
        setOpenActionJobId(null);
        setActionMenuPosition(null);
      }
    };

    if (openActionJobId) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openActionJobId]);

  // Fetch jobs once and share via React Query
  const {
    data: jobsData,
    isLoading: jobsLoading,
    error: _jobsError,
  } = useJobs(!!user);

  const jobs = jobsData || [];
  const loading = jobsLoading;

  // Calculate tab counts
  const tabCounts = useMemo(() => {
    const activeCount = jobs.filter((job) => job.status !== 0 && job.status !== 'closed').length;
    const closedCount = jobs.filter((job) => job.status === 0 || job.status === 'closed').length;
    return { active: activeCount, closed: closedCount };
  }, [jobs]);

  const tabsList = [
    { id: 'active', label: 'Active', count: tabCounts.active },
    { id: 'closed', label: 'Closed', count: tabCounts.closed },
  ];

  // Measure active tab for sliding underline
  useLayoutEffect(() => {
    const container = tabsRef.current;
    if (!container) return;
    const tabEl = container.querySelector(`[data-tab-id="${activeTab}"]`);
    if (!tabEl) return;
    const measure = () => {
      const left = tabEl.offsetLeft;
      const width = tabEl.offsetWidth;
      setIndicator((prev) => (prev.left === left && prev.width === width ? prev : { left, width }));
    };
    measure();
    const t1 = setTimeout(measure, 0);
    const t2 = setTimeout(measure, 50);
    const rafId = requestAnimationFrame(() => {
      measure();
      requestAnimationFrame(measure);
    });
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [activeTab]);

  // Filter jobs based on tab (active/closed), search and status
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const isClosed = job.status === 0 || job.status === 'closed';
      const matchesTab = activeTab === 'active' ? !isClosed : isClosed;

      const locationStr = Array.isArray(job.location) ? job.location.join(' ') : job.location || '';
      const creator = job.createdBy;
      const creatorStr = creator ? [creator.name, creator.email].filter(Boolean).join(' ') : '';
      const matchesSearch =
        searchTerm === '' ||
        job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        locationStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
        creatorStr.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
      // A job saved before storeFormat existed is EBO, as the server's payroll Download counts it.
      const matchesStoreFormat =
        !isGoColors ||
        storeFormatFilter === 'All' ||
        normaliseStoreFormat(job.storeFormat) === storeFormatFilter;

      return matchesTab && matchesSearch && matchesStatus && matchesStoreFormat;
    });
  }, [jobs, activeTab, searchTerm, statusFilter, isGoColors, storeFormatFilter]);

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) return 'less than a minute ago';
    if (diffMinutes < 60)
      return diffMinutes === 1 ? 'about 1 minute ago' : `about ${diffMinutes} minutes ago`;
    if (diffHours < 24)
      return diffHours === 1 ? 'about 1 hour ago' : `about ${diffHours} hours ago`;
    if (diffDays < 30) return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    if (diffMonths < 12) return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
    return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
  };

  const trackJobAccess = async (jobId) => {
    try {
      await axios.patch(`/api/job-posts/jobs/${jobId}`, {
        lastAccessedAt: new Date().toISOString(),
      });
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
    } catch (err) {
      console.error('Failed to update last accessed time:', err);
    }
  };

  const handleViewJobResults = (job) => {
    trackJobAccess(job._id);
    navigate(`/dashboard/job-posts/jobs/${job._id}/screenings`, {
      state: { job },
    });
  };

  const handleCloseClick = (e, jobId) => {
    e.stopPropagation();
    setCloseReasons([]);
    setCloseNote('');
    setShowCloseConfirm(jobId);
  };

  const handleCloseConfirm = async (jobId) => {
    try {
      setClosingJobId(jobId);
      await axios.patch(`/api/job-posts/jobs/${jobId}/close`, {
        reasons: closeReasons,
        note: closeNote,
      });

      toast.success('Job closed successfully');

      // Invalidate and refetch jobs
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setShowCloseConfirm(null);
      setCloseReasons([]);
      setCloseNote('');
    } catch (error) {
      console.error('Error closing job:', error);
      toast.error(error.response?.data?.message || 'Failed to close job posting');
    } finally {
      setClosingJobId(null);
    }
  };

  const handleCloseCancel = () => {
    setShowCloseConfirm(null);
    setCloseReasons([]);
    setCloseNote('');
  };

  const toggleCloseReason = (reason) => {
    setCloseReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  };

  const handleReopenClick = (e, jobId) => {
    e.stopPropagation();
    setShowReopenConfirm(jobId);
  };

  const handleReopenConfirm = async (jobId) => {
    try {
      setReopeningJobId(jobId);
      await axios.patch(`/api/job-posts/jobs/${jobId}/reopen`);

      toast.success('Job reopened successfully');

      // Invalidate and refetch jobs
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setShowReopenConfirm(null);
    } catch (error) {
      console.error('Error reopening job:', error);
      toast.error(error.response?.data?.message || 'Failed to reopen job posting');
    } finally {
      setReopeningJobId(null);
    }
  };

  const handleReopenCancel = () => {
    setShowReopenConfirm(null);
  };

  const handlePermanentDeleteClick = (e, jobId) => {
    e.stopPropagation();
    setShowDeleteConfirm(jobId);
  };

  const handlePermanentDeleteConfirm = async (jobId) => {
    try {
      setDeletingJobId(jobId);
      await axios.delete(`/api/job-posts/jobs/${jobId}`);

      toast.success('Job and all associated data permanently deleted');

      // Invalidate and refetch all related queries
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['assessments-batch'] });
      queryClient.invalidateQueries({ queryKey: ['ai-interviews-batch'] });
      queryClient.invalidateQueries({ queryKey: ['candidates-counts'] });
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error(error.response?.data?.message || 'Failed to delete job posting');
    } finally {
      setDeletingJobId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(null);
  };

  const handleOpenJobCreationModal = () => {
    // Reset form data when opening modal
    setJobFormData({
      title: '',
      description: '',
      keyResponsibilities: '',
      experience: '',
      skills: '',
      keyAttributes: '',
      location: '',
      locationType: 'remote',
      experienceMin: '',
      experienceMax: '',
      salaryMin: '',
      salaryMax: '',
      salaryCurrency: 'USD',
      education: '',
      candidateQualification: '',
      genderPreference: 'any',
      ageMin: '',
      ageMax: '',
    });
    setShowJobForm(false);
    setShowJobCreationModal(true);
  };

  const handleCloseJobCreationModal = () => {
    setShowJobCreationModal(false);
    setShowJobForm(false);
  };

  const handleJobCreated = async (job) => {
    // Close modal
    handleCloseJobCreationModal();
    // Refresh jobs list using React Query
    queryClient.invalidateQueries({ queryKey: ['jobs'] });
    queryClient.invalidateQueries({ queryKey: ['assessments-batch'] });
    queryClient.invalidateQueries({ queryKey: ['ai-interviews-batch'] });
    queryClient.invalidateQueries({ queryKey: ['candidates-counts'] });
  };

  const getCreatorName = (job) => {
    const creator = job.createdBy;
    if (!creator) return '—';
    return creator.name || creator.email || '—';
  };

  const getStatusBadge = (status) => {
    const normalized = typeof status === 'string' ? status.toLowerCase() : status;

    let label = 'Closed';
    let className =
      'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700';

    if (normalized === 1 || normalized === 'active' || normalized === 'posting') {
      label = 'Active';
      className =
        'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700';
    } else if (normalized === 'draft' || normalized === 'paused') {
      label = normalized === 'draft' ? 'Draft' : 'Paused';
      className =
        'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700';
    } else if (normalized === 0 || normalized === 'closed' || normalized === 'cancelled') {
      label = 'Closed';
      className =
        'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700';
    }

    return <span className={className}>{label}</span>;
  };

  const SkeletonBlock = ({ className = '' }) => (
    <div className={`animate-pulse rounded-md bg-gray-200/90 ${className}`} />
  );

  const tableSkeletonRows = 6;

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="min-h-screen bg-[#F7F9FC]"
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 pt-6 pb-12">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              AI Recruitment Platform
            </h1>
            <p className="mt-1.5 text-gray-600 text-sm sm:text-base">
              Manage jobs, screen candidates, and make better hiring decisions.
            </p>
          </div>
          {canCreateJob && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleOpenJobCreationModal}
              data-testid="create-job-btn"
              className="px-5 py-2.5 bg-transparent border-2 border-gray-900 text-gray-900 rounded-lg hover:bg-[#007BFF] hover:border-[#007BFF] hover:text-white flex items-center gap-2 font-semibold transition-all duration-200 text-sm shrink-0"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Create Job</span>
            </motion.button>
          )}
        </div>

        {/* Tabs - Lined style with sliding underline */}
        <div className="mb-6">
          <div ref={tabsRef} className="relative flex items-center gap-8 min-w-0">
            {tabsList.map((tab) => (
              <button
                key={tab.id}
                type="button"
                data-tab-id={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative z-10 pb-3 pt-0.5 text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  activeTab === tab.id ? 'text-[#242124]' : 'text-[#242124] hover:text-[#242124]'
                }`}
              >
                {tab.label}
                <span
                  className={`ml-2 py-0.5 px-2 rounded-full text-xs font-semibold ${
                    activeTab === tab.id
                      ? 'bg-[#8472B3]/10 text-[#8472B3]'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {loading ? <SkeletonBlock className="h-3 w-5 rounded-full" /> : tab.count}
                </span>
              </button>
            ))}
            <motion.div
              className="absolute bottom-0 left-0 h-0.5 bg-[#8472B3] z-0"
              initial={false}
              animate={{
                left: indicator.left,
                width: indicator.width > 0 ? indicator.width : activeTab === 'active' ? 80 : 90,
              }}
              transition={{
                type: 'spring',
                stiffness: 380,
                damping: 30,
              }}
              style={{ willChange: 'left, width' }}
            />
          </div>
        </div>

        {/* Search Bar and Go Colors Toggle */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 transition-colors duration-200 pointer-events-none" />
            <input
              type="text"
              placeholder="Search jobs by title, location, or created by..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-12 py-3 bg-white border-0 rounded-lg shadow-sm text-sm
                placeholder:text-gray-400
                focus:outline-none focus:ring-2 focus:ring-blue-500/30
                transition-colors duration-200 ease-out
                ${searchTerm ? 'pr-24' : 'pr-4'}`}
            />
            {searchTerm && (
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium tabular-nums">
                {filteredJobs.length} result{filteredJobs.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {isGoColors && (
            <div className="flex bg-white rounded-lg p-1 shadow-sm shrink-0 border border-gray-100">
              {['All', 'EBO', 'LFS'].map((format) => (
                <button
                  key={format}
                  onClick={() => setStoreFormatFilter(format)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    storeFormatFilter === format
                      ? 'bg-blue-50 text-blue-700 shadow-sm border border-blue-100'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  {format}
                </button>
              ))}
            </div>
          )}

          {/* Payroll Download across every job post of the chosen format; none under "All". */}
          {isGoColors && STORE_FORMATS.includes(storeFormatFilter) && (
            <div className="shrink-0 self-start md:self-center">
              <GoColorsPayrollDownloadMenu storeFormat={storeFormatFilter} />
            </div>
          )}
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="posting">Posting</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        )}

        {/* All Jobs Header */}
        <div className="px-6 py-4 bg-[#F7F9FC] mb-0">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-[#242124]">All Jobs</h2>
          </div>
        </div>

        {/* Jobs Table */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-[#F7F9FC] rounded-xl shadow-sm overflow-hidden"
        >
          {loading ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#007BFF]">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider rounded-tl-xl">
                      Job Title
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                      Location
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                      Created by
                    </th>
                    {(!isBurgerKing) && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                      SCREENINGS
                    </th>
                    )}
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">
                      CANDIDATES
                    </th>
                    {isBurgerKing && (
                      <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">
                        Undecided
                      </th>
                    )}
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">
                      {isSamtaGroup ? 'SHORTLISTED' : 'SELECTED'}
                    </th>
                    {isBurgerKing && (
                      <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">
                        Rejected
                      </th>
                    )}
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                      Created Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                      Last Activity
                    </th>
                    {isBurgerKing && (
                      <th
                        className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell"
                        title="Candidates who completed a screening with an AI assessment or AI interview on this job post. Candidates showing Not Completed are excluded. Unaffected by a later Select or Reject."
                      >
                        Charged
                      </th>
                    )}
                    <th className="px-6 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider w-14 rounded-tr-xl">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: tableSkeletonRows }).map((_, index) => (
                    <tr
                      key={`jobs-skeleton-row-${index}`}
                      className="bg-white border-b border-l border-r border-gray-300/50"
                    >
                      <td className="px-6 py-4">
                        <SkeletonBlock className="h-4 w-56 max-w-full mb-2" />
                        <div className="sm:hidden">
                          <SkeletonBlock className="h-3 w-40 max-w-full" />
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <SkeletonBlock className="h-4 w-40 max-w-full" />
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <SkeletonBlock className="h-4 w-28 max-w-full" />
                      </td>
                      {(!isBurgerKing) && (
                      <td className="px-4 py-4 text-center">
                        <SkeletonBlock className="h-7 w-10 rounded-md mx-auto" />
                      </td>
                      )}
                      <td className="px-4 py-4 text-center hidden md:table-cell">
                        <SkeletonBlock className="h-7 w-10 rounded-md mx-auto" />
                      </td>
                      {isBurgerKing && (
                        <td className="px-4 py-4 text-center hidden md:table-cell">
                          <SkeletonBlock className="h-7 w-10 rounded-md mx-auto" />
                        </td>
                      )}
                      <td className="px-4 py-4 text-center hidden md:table-cell">
                        <SkeletonBlock className="h-7 w-10 rounded-md mx-auto" />
                      </td>
                      {isBurgerKing && (
                        <td className="px-4 py-4 text-center hidden md:table-cell">
                          <SkeletonBlock className="h-7 w-10 rounded-md mx-auto" />
                        </td>
                      )}
                      <td className="px-6 py-4 hidden md:table-cell">
                        <SkeletonBlock className="h-4 w-16 max-w-full" />
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <SkeletonBlock className="h-4 w-24 max-w-full" />
                      </td>
                      {isBurgerKing && (
                        <td className="px-4 py-4 text-center hidden md:table-cell">
                          <SkeletonBlock className="h-7 w-10 rounded-md mx-auto" />
                        </td>
                      )}
                      <td className="px-6 py-4 text-right">
                        <SkeletonBlock className="h-8 w-8 rounded-lg ml-auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : filteredJobs.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16 px-6"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <BriefcaseIcon className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {jobs.length === 0 ? 'No job postings yet' : 'No jobs match your filters'}
              </h3>
              <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                {jobs.length === 0
                  ? 'Create your first job post to start receiving applications and screening candidates.'
                  : 'Try adjusting your search or switching tabs.'}
              </p>
              {jobs.length === 0 && canCreateJob && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleOpenJobCreationModal}
                  data-testid="create-job-btn"
                  className="px-5 py-2.5 bg-[#003153] text-white rounded-lg hover:bg-[#004175] flex items-center gap-2 font-semibold shadow-md mx-auto text-sm"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Create Your First Job Post
                </motion.button>
              )}
            </motion.div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#007BFF]">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider rounded-tl-xl">
                      Job Title
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                      Location
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                      Created by
                    </th>
                    {(!isBurgerKing) && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                      SCREENINGS
                    </th>
                    )}
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">
                      CANDIDATES
                    </th>
                    {isBurgerKing && (
                      <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">
                        Undecided
                      </th>
                    )}
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">
                      {isSamtaGroup ? 'SHORTLISTED' : 'SELECTED'}
                    </th>
                    {isBurgerKing && (
                      <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">
                        Rejected
                      </th>
                    )}
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                      Created Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                      Last Activity
                    </th>
                    {isBurgerKing && (
                      <th
                        className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell"
                        title="Candidates who completed a screening with an AI assessment or AI interview on this job post. Candidates showing Not Completed are excluded. Unaffected by a later Select or Reject."
                      >
                        Charged
                      </th>
                    )}
                    <th className="px-6 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider w-14 rounded-tr-xl">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.map((job) => {
                    const screeningsCount = job.screeningsCount ?? 0;
                    const locationDisplay = Array.isArray(job.location)
                      ? job.location.slice(0, 2).join(', ')
                      : job.location || '-';
                    return (
                      <tr
                        key={job._id}
                        className={`group transition-colors bg-white border-b border-l border-r border-gray-300/50 ${
                          activeTab === 'active'
                            ? 'cursor-pointer hover:bg-[#8472B3]/10'
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={
                          activeTab === 'active' ? () => handleViewJobResults(job) : undefined
                        }
                      >
                          <td className="px-6 py-4">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-[#242124] group-hover:text-[#242124] transition-colors line-clamp-2">
                                  {job.title}
                                </span>
                                {isGoColors && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    {job.storeFormat === 'LFS' ? 'LFS' : 'EBO'}
                                  </span>
                                )}
                              </div>
                              <div className="sm:hidden text-xs text-[#242124] mt-0.5">
                                {getCreatorName(job)} · {formatDate(job.createdAt)} ·{' '}
                                {formatRelativeTime(job.lastAccessedAt || job.updatedAt)} ·{' '}
                                {(!isBurgerKing) ? `${screeningsCount} screenings · ` : ''}{job.candidatesCount ?? 0} candidates · {job.shortlistedCount ?? 0} selected
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <div className="text-sm text-[#242124] line-clamp-2 max-w-[200px]">
                              {locationDisplay}
                            </div>
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <span className="text-sm text-[#242124]">{getCreatorName(job)}</span>
                          </td>
                          {(!isBurgerKing) && (
                          <td className="px-4 py-4 text-center">
                            <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-1 rounded-md bg-gray-100 text-sm font-medium text-[#242124]">
                              {screeningsCount}
                            </span>
                          </td>
                          )}
                          <td className="px-4 py-4 text-center hidden md:table-cell">
                            <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-1 rounded-md bg-blue-50 text-sm font-medium text-blue-700">
                              {job.candidatesCount ?? 0}
                            </span>
                          </td>
                          {isBurgerKing && (
                            <td className="px-4 py-4 text-center hidden md:table-cell">
                              <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-1 rounded-md bg-purple-50 text-sm font-medium text-purple-700">
                                {Math.max(0, (job.candidatesCount ?? 0) - (job.shortlistedCount ?? 0) - (job.rejectedCount ?? 0))}
                              </span>
                            </td>
                          )}
                          <td className="px-4 py-4 text-center hidden md:table-cell">
                            <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-1 rounded-md bg-green-50 text-sm font-medium text-green-700">
                              {job.shortlistedCount ?? 0}
                            </span>
                          </td>
                          {isBurgerKing && (
                            <td className="px-4 py-4 text-center hidden md:table-cell">
                              <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-1 rounded-md bg-rose-50 text-sm font-medium text-rose-700">
                                {job.rejectedCount ?? 0}
                              </span>
                            </td>
                          )}
                          <td className="px-6 py-4 hidden md:table-cell">
                            <span className="text-sm text-[#242124]">
                              {formatDate(job.createdAt)}
                            </span>
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <span className="text-sm text-[#242124]">
                              {formatRelativeTime(job.lastAccessedAt || job.updatedAt)}
                            </span>
                          </td>
                          {isBurgerKing && (
                            <td className="px-4 py-4 text-center hidden md:table-cell">
                              <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-1 rounded-md bg-emerald-50 text-sm font-medium text-emerald-700">
                                {job.chargedCount ?? 0}
                              </span>
                            </td>
                          )}
                          <td className="px-6 py-4 text-right relative">
                            <div className="relative action-menu-container">
                              {(canCloseJob || canReopenJob || canDeleteJob) && (
                                <button
                                  ref={(el) => (actionButtonRefs.current[job._id] = el)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (openActionJobId === job._id) {
                                      setOpenActionJobId(null);
                                      setActionMenuPosition(null);
                                    } else {
                                      const button = actionButtonRefs.current[job._id];
                                      if (button) {
                                        const rect = button.getBoundingClientRect();
                                        setActionMenuPosition({
                                          top: rect.top,
                                          right: window.innerWidth - rect.right,
                                        });
                                      }
                                      setOpenActionJobId(job._id);
                                    }
                                  }}
                                  className="text-gray-400 hover:text-gray-700 inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100 transition-colors"
                                  title="Actions"
                                >
                                  <EllipsisVerticalIcon className="h-5 w-5" />
                                </button>
                              )}
                              {openActionJobId === job._id &&
                                actionMenuPosition &&
                                (canCloseJob || canReopenJob || canDeleteJob) &&
                                createPortal(
                                  <div
                                    data-action-menu
                                    className="fixed min-w-[180px] bg-white rounded-xl shadow-lg border border-gray-100 z-[9999] overflow-hidden"
                                    style={{
                                      top: `${actionMenuPosition.top}px`,
                                      right: `${actionMenuPosition.right}px`,
                                      transform: 'translateY(calc(-100% - 8px))',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {activeTab === 'active' ? (
                                      <>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewJobResults(job);
                                            setOpenActionJobId(null);
                                            setActionMenuPosition(null);
                                          }}
                                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                                        >
                                          <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400 shrink-0" />
                                          <span>View screenings</span>
                                        </button>
                                        {canCloseJob && (
                                          <button
                                            onClick={(e) => {
                                              handleCloseClick(e, job._id);
                                              setOpenActionJobId(null);
                                              setActionMenuPosition(null);
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50 flex items-center gap-3 transition-colors border-t border-gray-100"
                                          >
                                            <XMarkIcon className="h-4 w-4 text-amber-500 shrink-0" />
                                            <span>Close job</span>
                                          </button>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        {canReopenJob && (
                                          <button
                                            onClick={(e) => {
                                              handleReopenClick(e, job._id);
                                              setOpenActionJobId(null);
                                              setActionMenuPosition(null);
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-3 transition-colors"
                                          >
                                            <ArrowPathIcon className="h-4 w-4 shrink-0" />
                                            <span>Reopen job</span>
                                          </button>
                                        )}
                                        {canDeleteJob && (
                                          <button
                                            onClick={(e) => {
                                              handlePermanentDeleteClick(e, job._id);
                                              setOpenActionJobId(null);
                                              setActionMenuPosition(null);
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors border-t border-gray-100"
                                          >
                                            <TrashIcon className="h-4 w-4 shrink-0" />
                                            <span>Permanently delete</span>
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>,
                                  document.body
                                )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>

        {/* Close Job — Reasons Modal */}
        {showCloseConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div
              className="absolute inset-0 bg-gray-900/50"
              onClick={() => !closingJobId && handleCloseCancel()}
            />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md z-10">
              {/* Header */}
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-yellow-100">
                    <XMarkIcon className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Close Job Posting</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Please tell us why you&apos;re closing this job. Select all that apply.
                    </p>
                  </div>
                </div>
              </div>

              {/* Checkboxes */}
              <div className="px-6 space-y-2.5">
                {CLOSE_REASON_OPTIONS.map((reason) => (
                  <label
                    key={reason}
                    className="flex items-center gap-3 cursor-pointer group"
                  >
                    <input
                      type="checkbox"
                      checked={closeReasons.includes(reason)}
                      onChange={() => toggleCloseReason(reason)}
                      disabled={!!closingJobId}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">{reason}</span>
                  </label>
                ))}
              </div>

              {/* Optional textarea */}
              <div className="px-6 mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional details <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={closeNote}
                  onChange={(e) => setCloseNote(e.target.value)}
                  disabled={!!closingJobId}
                  placeholder="Any other context about why you're closing this role..."
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              {/* Info note */}
              <div className="px-6 mt-3">
                <div className="rounded-lg bg-blue-50 px-3 py-2.5">
                  <p className="text-xs text-blue-700">
                    <strong>Note:</strong> All screenings, candidates, assessments and interviews will be preserved. You can reopen this job at any time.
                  </p>
                </div>
              </div>

              {/* Footer buttons */}
              <div className="px-6 py-4 mt-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={handleCloseCancel}
                  disabled={!!closingJobId}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleCloseConfirm(showCloseConfirm)}
                  disabled={closeReasons.length === 0 || !!closingJobId}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {closingJobId === showCloseConfirm ? 'Closing...' : 'Close Job'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reopen Confirmation Modal */}
        {showReopenConfirm && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-50"
                onClick={handleReopenCancel}
              ></div>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg
                        className="h-6 w-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Reopen Job Posting
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to reopen this job posting? This will move the job
                          back to the Active tab and make it available for new candidates and
                          screenings.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    onClick={() => handleReopenConfirm(showReopenConfirm)}
                    disabled={reopeningJobId === showReopenConfirm}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reopeningJobId === showReopenConfirm ? 'Reopening...' : 'Reopen Job'}
                  </button>
                  <button
                    onClick={handleReopenCancel}
                    disabled={reopeningJobId === showReopenConfirm}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Permanent Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-50"
                onClick={handleDeleteCancel}
              ></div>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <TrashIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Permanently Delete Job Posting
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to permanently delete this job posting? This action
                          will permanently delete:
                        </p>
                        <ul className="mt-2 text-sm text-gray-500 list-disc list-inside">
                          <li>The job posting</li>
                          <li>All associated screenings</li>
                          <li>All associated candidates</li>
                          <li>All associated assessments</li>
                          <li>All associated AI interviews</li>
                        </ul>
                        <p className="mt-2 text-sm font-semibold text-red-600">
                          This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    onClick={() => handlePermanentDeleteConfirm(showDeleteConfirm)}
                    disabled={deletingJobId === showDeleteConfirm}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deletingJobId === showDeleteConfirm ? 'Deleting...' : 'Delete Permanently'}
                  </button>
                  <button
                    onClick={handleDeleteCancel}
                    disabled={deletingJobId === showDeleteConfirm}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Job Creation Modal */}
        <AnimatedModal
          isOpen={showJobCreationModal}
          onClose={handleCloseJobCreationModal}
          contentClassName="bg-white rounded-2xl shadow-2xl w-full max-w-2xl"
          zIndex={50}
        >
          <div className="px-6 py-5">
            <JobCreationForm
              formData={jobFormData}
              setFormData={setJobFormData}
              showForm={showJobForm}
              setShowForm={setShowJobForm}
              onJobCreated={handleJobCreated}
              hideBackButton={false}
              stepLabel="Job Details"
            />
          </div>
        </AnimatedModal>
      </div>
    </motion.div>
  );
};

export default ResultsAnalytics;
